import type { RunLogFile } from '../interfaces/ui';

const OBJECT_URL_RELEASE_DELAY_MS = 1000;

function safeFileName(value: string): string {
  return (
    value
      .replace(/[<>:"/\\|?*\u0000-\u001f\u007f]/g, '_')
      .replace(/\.{2,}/g, '_')
      .replace(/[. ]+$/g, '') || 'log'
  );
}

export function getRunLogFileNames(
  runId: string,
  files: RunLogFile[],
): string[] {
  const usedNames = new Set<string>();
  return files.map(file => {
    const baseName = `${safeFileName(runId)}_${safeFileName(file.fileName)}`;
    const extensionIndex = baseName.lastIndexOf('.');
    const stem =
      extensionIndex > 0 ? baseName.slice(0, extensionIndex) : baseName;
    const extension = extensionIndex > 0 ? baseName.slice(extensionIndex) : '';
    let fileName = baseName;
    let suffix = 2;
    while (usedNames.has(fileName.toLowerCase())) {
      fileName = `${stem}_${suffix}${extension}`;
      suffix += 1;
    }
    usedNames.add(fileName.toLowerCase());
    return fileName;
  });
}

export function downloadRunLogBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  let initiated = false;
  try {
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    initiated = true;
  } finally {
    anchor.remove();
    if (initiated) {
      window.setTimeout(
        () => URL.revokeObjectURL(objectUrl),
        OBJECT_URL_RELEASE_DELAY_MS,
      );
    } else {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

export async function downloadRunLogs(
  runId: string,
  files: RunLogFile[],
  fileNames = getRunLogFileNames(runId, files),
): Promise<void> {
  if (!files.length) throw new Error('Select at least one log file.');
  if (files.length !== fileNames.length)
    throw new Error('Invalid log filenames.');
  if (files.length === 1) {
    downloadRunLogBlob(
      new Blob([files[0].content], { type: 'text/plain;charset=utf-8' }),
      fileNames[0],
    );
    return;
  }
  const { default: JSZip } = await import('jszip');
  const archive = new JSZip();
  files.forEach((file, index) => archive.file(fileNames[index], file.content));
  const blob = await archive.generateAsync({ type: 'blob' });
  downloadRunLogBlob(blob, `${safeFileName(runId)}_logs.zip`);
}
